import React, { useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { SearchIcon, MailIcon, BarChart3Icon, CheckCircle2Icon, CheckIcon, SendIcon } from 'lucide-react';
type Step = 'search' | 'create' | 'track';
export function SequenceDemo() {
  const [activeStep, setActiveStep] = useState<Step>('search');
  const [searchPhase, setSearchPhase] = useState<'loading' | 'done'>('loading');
  const [createPhase, setCreatePhase] = useState<'loading' | 'done'>('loading');
  const [trackPhase, setTrackPhase] = useState<'loading' | 'done'>('loading');
  const searchTimerRef = useRef<NodeJS.Timeout | null>(null);
  const createTimerRef = useRef<NodeJS.Timeout | null>(null);
  const trackTimerRef = useRef<NodeJS.Timeout | null>(null);
  useEffect(() => {
    startSearch();
    return () => {
      if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
      if (createTimerRef.current) clearTimeout(createTimerRef.current);
      if (trackTimerRef.current) clearTimeout(trackTimerRef.current);
    };
  }, []);
  const startSearch = () => {
    setSearchPhase('loading');
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(() => setSearchPhase('done'), 1200);
  };
  const startCreate = () => {
    setCreatePhase('loading');
    if (createTimerRef.current) clearTimeout(createTimerRef.current);
    createTimerRef.current = setTimeout(() => setCreatePhase('done'), 1400);
  };
  const startTrack = () => {
    setTrackPhase('loading');
    if (trackTimerRef.current) clearTimeout(trackTimerRef.current);
    trackTimerRef.current = setTimeout(() => setTrackPhase('done'), 1500);
  };
  const handleTabSwitch = (step: Step) => {
    setActiveStep(step);
    if (step === 'search') startSearch();
    if (step === 'create') startCreate();
    if (step === 'track') startTrack();
  };
  return <section className="py-24 bg-white" data-id="element-1364">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1365">
        <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-1366">
          <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-6 leading-tight" data-id="element-1367">
            See <span className="text-blue-600" data-id="element-1368">email sequencing</span> in
            action
          </h2>
          <p className="text-xl text-slate-600 leading-relaxed" data-id="element-1369">
            Find leads, build B2B email sequences, and track results — all from
            one platform.
          </p>
        </div>

        <div className="flex flex-wrap justify-center gap-3 mb-14" data-id="element-1370">
          <button onClick={() => handleTabSwitch('search')} className={`flex items-center gap-2 px-6 py-3 rounded-full text-base font-semibold transition-all duration-200 ${activeStep === 'search' ? 'bg-slate-900 text-white' : 'bg-slate-50 text-slate-600 hover:bg-slate-100'}`} data-id="element-1371">
            <SearchIcon className="w-4 h-4" data-id="element-1372" />
            Build prospect list
          </button>
          <button onClick={() => handleTabSwitch('create')} className={`flex items-center gap-2 px-6 py-3 rounded-full text-base font-semibold transition-all duration-200 ${activeStep === 'create' ? 'bg-slate-900 text-white' : 'bg-slate-50 text-slate-600 hover:bg-slate-100'}`} data-id="element-1373">
            <MailIcon className="w-4 h-4" data-id="element-1374" />
            Create sequence
          </button>
          <button onClick={() => handleTabSwitch('track')} className={`flex items-center gap-2 px-6 py-3 rounded-full text-base font-semibold transition-all duration-200 ${activeStep === 'track' ? 'bg-slate-900 text-white' : 'bg-slate-50 text-slate-600 hover:bg-slate-100'}`} data-id="element-1375">
            <BarChart3Icon className="w-4 h-4" data-id="element-1376" />
            Track results
          </button>
        </div>

        <AnimatePresence mode="wait" data-id="element-1377">
          {activeStep === 'search' && <motion.div key="search" initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -10
        }} transition={{
          duration: 0.25
        }} className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start" data-id="element-1378">
              <div className="lg:col-span-4 bg-slate-50 rounded-[2.5rem] p-8 sm:p-10" data-id="element-1379">
                <h3 className="text-lg font-bold text-slate-900 mb-1" data-id="element-1380">
                  Search filters
                </h3>
                <p className="text-sm text-slate-500 mb-8" data-id="element-1381">
                  Find local businesses to target.
                </p>
                <div className="space-y-5" data-id="element-1382">
                  <div data-id="element-1383">
                    <label className="block text-sm font-semibold text-slate-700 mb-1.5" data-id="element-1384">
                      Industry
                    </label>
                    <div className="w-full px-5 py-3.5 bg-white rounded-2xl text-base shadow-sm text-slate-700" data-id="element-1385">
                      Restaurants & Cafes
                    </div>
                  </div>
                  <div data-id="element-1386">
                    <label className="block text-sm font-semibold text-slate-700 mb-1.5" data-id="element-1387">
                      Location
                    </label>
                    <div className="w-full px-5 py-3.5 bg-white rounded-2xl text-base shadow-sm text-slate-700" data-id="element-1388">
                      Austin, TX
                    </div>
                  </div>
                </div>
                <div className="mt-8 flex items-center justify-center gap-2 py-4 text-sm font-semibold" data-id="element-1389">
                  {searchPhase === 'loading' ? <span className="flex items-center gap-2 text-blue-600" data-id="element-1390">
                      <motion.div animate={{
                  rotate: 360
                }} transition={{
                  repeat: Infinity,
                  duration: 1,
                  ease: 'linear'
                }} className="w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full" data-id="element-1391" />
                      Searching database...
                    </span> : <span className="flex items-center gap-2 text-green-600" data-id="element-1392">
                      <CheckCircle2Icon className="w-4 h-4" data-id="element-1393" />
                      Found 842 prospects
                    </span>}
                </div>
              </div>
              <div className="lg:col-span-8 bg-slate-50 rounded-[2.5rem] p-8 sm:p-10 min-h-[420px]" data-id="element-1394">
                {searchPhase === 'loading' && <div className="h-full min-h-[350px] flex flex-col items-center justify-center" data-id="element-1395">
                    <motion.div animate={{
                rotate: 360
              }} transition={{
                repeat: Infinity,
                duration: 1,
                ease: 'linear'
              }} className="w-10 h-10 rounded-full mb-4" style={{
                borderWidth: '3px',
                borderColor: '#e2e8f0',
                borderTopColor: '#156ef6',
                borderStyle: 'solid'
              }} data-id="element-1396" />
                    <p className="text-lg font-medium text-slate-500" data-id="element-1397">
                      Finding local business leads...
                    </p>
                  </div>}
                {searchPhase === 'done' && <motion.div initial={{
              opacity: 0
            }} animate={{
              opacity: 1
            }} transition={{
              duration: 0.4
            }} data-id="element-1398">
                    <div className="flex items-center justify-between mb-6" data-id="element-1399">
                      <h3 className="text-lg font-bold text-slate-900" data-id="element-1400">
                        Prospect list ready
                      </h3>
                    </div>
                    <div className="space-y-2 max-h-[420px] overflow-y-auto pr-1" data-id="element-1401">
                      {[{
                  name: "Joe's Pizza",
                  owner: 'Joe Martinez',
                  email: 'joe@joespizza.com',
                  phone: '(512) 555-0134',
                  location: 'Austin, TX',
                  employees: '12',
                  revenue: '$1.8M'
                }, {
                  name: 'Austin Coffee Co',
                  owner: 'Sarah Chen',
                  email: 'sarah@austincoffee.com',
                  phone: '(512) 555-0267',
                  location: 'Austin, TX',
                  employees: '8',
                  revenue: '$950K'
                }, {
                  name: 'Texas BBQ Joint',
                  owner: 'Marcus Williams',
                  email: 'marcus@texasbbq.com',
                  phone: '(512) 555-0391',
                  location: 'Round Rock, TX',
                  employees: '22',
                  revenue: '$2.4M'
                }, {
                  name: 'Lone Star Tacos',
                  owner: 'Maria Gonzalez',
                  email: 'maria@lonestartacos.com',
                  phone: '(512) 555-0478',
                  location: 'Austin, TX',
                  employees: '15',
                  revenue: '$1.2M'
                }, {
                  name: 'Hill Country Bakery',
                  owner: 'Emily Brooks',
                  email: 'emily@hillcountrybakery.com',
                  phone: '(512) 555-0512',
                  location: 'Cedar Park, TX',
                  employees: '6',
                  revenue: '$680K'
                }, {
                  name: 'Capital City Sushi',
                  owner: 'David Tanaka',
                  email: 'david@capitalcitysushi.com',
                  phone: '(512) 555-0645',
                  location: 'Austin, TX',
                  employees: '18',
                  revenue: '$2.1M'
                }, {
                  name: 'Riverside Diner',
                  owner: 'Tom Henderson',
                  email: 'tom@riversidediner.com',
                  phone: '(512) 555-0723',
                  location: 'Pflugerville, TX',
                  employees: '10',
                  revenue: '$1.1M'
                }].map((prospect, idx) => <motion.div key={idx} initial={{
                  opacity: 0,
                  y: 10
                }} animate={{
                  opacity: 1,
                  y: 0
                }} transition={{
                  delay: idx * 0.08,
                  duration: 0.3
                }} className="bg-white rounded-xl p-4 shadow-sm" data-id="element-1402">
                          <div className="flex items-start justify-between mb-2" data-id="element-1403">
                            <div data-id="element-1404">
                              <h4 className="text-sm font-bold text-slate-900" data-id="element-1405">
                                {prospect.name}
                              </h4>
                              <p className="text-xs text-slate-500 mt-0.5" data-id="element-1406">
                                {prospect.owner} · {prospect.location}
                              </p>
                            </div>
                            <div className="flex items-center gap-1 bg-green-50 text-green-700 px-1.5 py-0.5 rounded text-[10px] font-bold uppercase shrink-0" data-id="element-1407">
                              <CheckIcon className="w-3 h-3" data-id="element-1408" /> Verified
                            </div>
                          </div>
                          <div className="flex flex-wrap gap-x-3 gap-y-1 text-[11px] text-slate-500" data-id="element-1409">
                            <span className="flex items-center gap-1" data-id="element-1410">
                              <MailIcon className="w-3 h-3 text-blue-400" data-id="element-1411" />
                              {prospect.email}
                            </span>
                            <span data-id="element-1412">{prospect.phone}</span>
                            <span className="text-slate-300" data-id="element-1413">·</span>
                            <span data-id="element-1414">{prospect.employees} employees</span>
                            <span className="text-slate-300" data-id="element-1415">·</span>
                            <span data-id="element-1416">{prospect.revenue} rev</span>
                          </div>
                        </motion.div>)}
                    </div>
                  </motion.div>}
              </div>
            </motion.div>}

          {activeStep === 'create' && <motion.div key="create" initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -10
        }} transition={{
          duration: 0.25
        }} className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start" data-id="element-1417">
              <div className="lg:col-span-4 bg-slate-50 rounded-[2.5rem] p-8 sm:p-10" data-id="element-1418">
                <h3 className="text-lg font-bold text-slate-900 mb-1" data-id="element-1419">
                  Sequence settings
                </h3>
                <p className="text-sm text-slate-500 mb-8" data-id="element-1420">
                  Configure your email sequence tools.
                </p>
                <div className="space-y-3" data-id="element-1421">
                  <div className="flex items-center justify-between p-3 bg-white rounded-xl border border-slate-100" data-id="element-1422">
                    <span className="text-sm font-medium text-slate-700" data-id="element-1423">
                      Step 1: Initial outreach
                    </span>
                    <span className="text-xs font-bold text-blue-600 bg-blue-50 px-2 py-1 rounded" data-id="element-1424">
                      Day 1
                    </span>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-white rounded-xl border border-slate-100" data-id="element-1425">
                    <span className="text-sm font-medium text-slate-700" data-id="element-1426">
                      Step 2: First Follow-up
                    </span>
                    <span className="text-xs font-bold text-slate-600 bg-slate-100 px-2 py-1 rounded" data-id="element-1427">
                      Day 4
                    </span>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-white rounded-xl border border-slate-100" data-id="element-1428">
                    <span className="text-sm font-medium text-slate-700" data-id="element-1429">
                      Step 3: Second Follow-up
                    </span>
                    <span className="text-xs font-bold text-slate-600 bg-slate-100 px-2 py-1 rounded" data-id="element-1430">
                      Day 7
                    </span>
                  </div>
                </div>
                <div className="mt-8 flex items-center justify-center gap-2 py-4 text-sm font-semibold" data-id="element-1431">
                  {createPhase === 'loading' ? <span className="flex items-center gap-2 text-blue-600" data-id="element-1432">
                      <motion.div animate={{
                  rotate: 360
                }} transition={{
                  repeat: Infinity,
                  duration: 1,
                  ease: 'linear'
                }} className="w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full" data-id="element-1433" />
                      Generating previews...
                    </span> : <span className="flex items-center gap-2 text-green-600" data-id="element-1434">
                      <CheckCircle2Icon className="w-4 h-4" data-id="element-1435" />
                      Sequence ready
                    </span>}
                </div>
              </div>
              <div className="lg:col-span-8 bg-slate-50 rounded-[2.5rem] p-8 sm:p-10 min-h-[420px]" data-id="element-1436">
                {createPhase === 'loading' && <div className="h-full min-h-[350px] flex flex-col items-center justify-center" data-id="element-1437">
                    <motion.div animate={{
                rotate: 360
              }} transition={{
                repeat: Infinity,
                duration: 1,
                ease: 'linear'
              }} className="w-10 h-10 rounded-full mb-4" style={{
                borderWidth: '3px',
                borderColor: '#e2e8f0',
                borderTopColor: '#156ef6',
                borderStyle: 'solid'
              }} data-id="element-1438" />
                    <p className="text-lg font-medium text-slate-500" data-id="element-1439">
                      Applying personalization variables...
                    </p>
                  </div>}
                {createPhase === 'done' && <motion.div initial={{
              opacity: 0
            }} animate={{
              opacity: 1
            }} transition={{
              duration: 0.4
            }} data-id="element-1440">
                    <div className="flex items-center justify-between mb-6" data-id="element-1441">
                      <h3 className="text-lg font-bold text-slate-900" data-id="element-1442">
                        Email preview
                      </h3>
                    </div>
                    <div className="space-y-4" data-id="element-1443">
                      <motion.div initial={{
                  opacity: 0,
                  y: 10
                }} animate={{
                  opacity: 1,
                  y: 0
                }} transition={{
                  delay: 0.15,
                  duration: 0.3
                }} className="bg-white rounded-2xl p-6 shadow-sm border border-blue-100" data-id="element-1444">
                        <div className="mb-4 pb-4 border-b border-slate-100" data-id="element-1445">
                          <div className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-1" data-id="element-1446">
                            Subject
                          </div>
                          <div className="text-sm font-semibold text-slate-900" data-id="element-1447">
                            Question about Joe's Pizza
                          </div>
                        </div>
                        <div data-id="element-1448">
                          <div className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-2" data-id="element-1449">
                            Body
                          </div>
                          <div className="text-sm text-slate-700 leading-relaxed" data-id="element-1450">
                            Hi Joe,
                            <br data-id="element-1451" />
                            <br data-id="element-1452" />
                            I noticed that Joe's Pizza is currently using Toast
                            POS in Austin, TX.
                            <br data-id="element-1453" />
                            <br data-id="element-1454" />
                            We help restaurants like yours streamline
                            operations. Open to a quick chat?
                          </div>
                        </div>
                      </motion.div>
                    </div>
                  </motion.div>}
              </div>
            </motion.div>}

          {activeStep === 'track' && <motion.div key="track" initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -10
        }} transition={{
          duration: 0.25
        }} className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start" data-id="element-1455">
              <div className="lg:col-span-4 bg-slate-50 rounded-[2.5rem] p-8 sm:p-10" data-id="element-1456">
                <h3 className="text-lg font-bold text-slate-900 mb-1" data-id="element-1457">
                  Campaign stats
                </h3>
                <p className="text-sm text-slate-500 mb-8" data-id="element-1458">
                  Monitor your B2B email sequence.
                </p>
                <div className="space-y-5" data-id="element-1459">
                  <div data-id="element-1460">
                    <label className="block text-sm font-semibold text-slate-700 mb-1.5" data-id="element-1461">
                      Enrolled prospects
                    </label>
                    <div className="w-full px-5 py-3.5 bg-white rounded-2xl text-base shadow-sm text-slate-700 font-bold" data-id="element-1462">
                      842
                    </div>
                  </div>
                  <div data-id="element-1463">
                    <label className="block text-sm font-semibold text-slate-700 mb-1.5" data-id="element-1464">
                      CRM Sync
                    </label>
                    <div className="w-full px-5 py-3.5 bg-white rounded-2xl text-base shadow-sm text-green-600 font-bold flex items-center gap-2 border-2 border-green-100" data-id="element-1465">
                      <CheckCircle2Icon className="w-4 h-4" data-id="element-1466" /> Active
                    </div>
                  </div>
                </div>
                <div className="mt-8 flex items-center justify-center gap-2 py-4 text-sm font-semibold" data-id="element-1467">
                  {trackPhase === 'loading' ? <span className="flex items-center gap-2 text-blue-600" data-id="element-1468">
                      <motion.div animate={{
                  rotate: 360
                }} transition={{
                  repeat: Infinity,
                  duration: 1,
                  ease: 'linear'
                }} className="w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full" data-id="element-1469" />
                      Fetching analytics...
                    </span> : <span className="flex items-center gap-2 text-green-600" data-id="element-1470">
                      <CheckCircle2Icon className="w-4 h-4" data-id="element-1471" />
                      Live data loaded
                    </span>}
                </div>
              </div>
              <div className="lg:col-span-8 bg-slate-50 rounded-[2.5rem] p-8 sm:p-10 min-h-[420px]" data-id="element-1472">
                {trackPhase === 'loading' && <div className="h-full min-h-[350px] flex flex-col items-center justify-center" data-id="element-1473">
                    <motion.div animate={{
                rotate: 360
              }} transition={{
                repeat: Infinity,
                duration: 1,
                ease: 'linear'
              }} className="w-10 h-10 rounded-full mb-4" style={{
                borderWidth: '3px',
                borderColor: '#e2e8f0',
                borderTopColor: '#156ef6',
                borderStyle: 'solid'
              }} data-id="element-1474" />
                    <p className="text-lg font-medium text-slate-500" data-id="element-1475">
                      Loading sequence performance...
                    </p>
                  </div>}
                {trackPhase === 'done' && <motion.div initial={{
              opacity: 0
            }} animate={{
              opacity: 1
            }} transition={{
              duration: 0.4
            }} data-id="element-1476">
                    {/* Header */}
                    <div className="flex items-center justify-between mb-8" data-id="element-1477">
                      <h3 className="text-lg font-bold text-slate-900" data-id="element-1478">
                        Sequence performance
                      </h3>
                      <div className="flex items-center gap-1.5 bg-green-50 text-green-700 px-2.5 py-1 rounded-full text-xs font-bold" data-id="element-1479">
                        <span className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse" data-id="element-1480" />
                        Live
                      </div>
                    </div>

                    {/* 3 Key Metrics */}
                    <div className="grid grid-cols-3 gap-3 mb-8" data-id="element-1481">
                      {[{
                  label: 'Emails sent',
                  value: '3,540',
                  icon: SendIcon
                }, {
                  label: 'Open rate',
                  value: '42.8%',
                  icon: MailIcon
                }, {
                  label: 'Reply rate',
                  value: '12.4%',
                  icon: CheckCircle2Icon
                }].map((m, i) => {
                  const MIcon = m.icon;
                  return <motion.div key={i} initial={{
                    opacity: 0,
                    y: 12
                  }} animate={{
                    opacity: 1,
                    y: 0
                  }} transition={{
                    delay: i * 0.1,
                    duration: 0.4,
                    ease: 'easeOut'
                  }} className="bg-white rounded-2xl p-5 shadow-sm border border-slate-100 text-center" data-id="element-1482">
                            <div className="w-8 h-8 rounded-xl bg-blue-50 flex items-center justify-center mx-auto mb-3" data-id="element-1483">
                              <MIcon className="w-4 h-4 text-blue-600" data-id="element-1484" />
                            </div>
                            <div className="text-2xl font-bold text-slate-900" data-id="element-1485">
                              {m.value}
                            </div>
                            <div className="text-xs text-slate-400 mt-1 font-medium" data-id="element-1486">
                              {m.label}
                            </div>
                          </motion.div>;
                })}
                    </div>

                    {/* Simple Funnel Bars */}
                    <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100 mb-6" data-id="element-1487">
                      <div className="text-xs font-semibold text-slate-700 mb-5" data-id="element-1488">
                        Engagement funnel
                      </div>
                      <div className="space-y-4" data-id="element-1489">
                        {[{
                    label: 'Sent',
                    value: 3540,
                    pct: 100,
                    color: 'bg-slate-800'
                  }, {
                    label: 'Opened',
                    value: 1515,
                    pct: 42.8,
                    color: 'bg-blue-500'
                  }, {
                    label: 'Clicked',
                    value: 648,
                    pct: 18.3,
                    color: 'bg-blue-400'
                  }, {
                    label: 'Replied',
                    value: 439,
                    pct: 12.4,
                    color: 'bg-emerald-500'
                  }].map((step, i) => <motion.div key={i} initial={{
                    opacity: 0,
                    x: -10
                  }} animate={{
                    opacity: 1,
                    x: 0
                  }} transition={{
                    delay: 0.2 + i * 0.1,
                    duration: 0.35,
                    ease: 'easeOut'
                  }} data-id="element-1490">
                            <div className="flex items-center justify-between mb-1.5" data-id="element-1491">
                              <span className="text-sm font-medium text-slate-700" data-id="element-1492">
                                {step.label}
                              </span>
                              <span className="text-sm font-bold text-slate-900" data-id="element-1493">
                                {step.value.toLocaleString()}
                                <span className="text-xs font-normal text-slate-400 ml-1" data-id="element-1494">
                                  ({step.pct}%)
                                </span>
                              </span>
                            </div>
                            <div className="h-2.5 bg-slate-100 rounded-full overflow-hidden" data-id="element-1495">
                              <motion.div initial={{
                        width: 0
                      }} animate={{
                        width: `${step.pct}%`
                      }} transition={{
                        delay: 0.3 + i * 0.12,
                        duration: 0.6,
                        ease: 'easeOut'
                      }} className={`h-full rounded-full ${step.color}`} data-id="element-1496" />
                            </div>
                          </motion.div>)}
                      </div>
                    </div>

                    {/* CTA */}
                    <a href="http://app.openmart.com/register" target="_blank" rel="noopener noreferrer" className="w-full px-6 py-3.5 bg-blue-600 text-white font-bold rounded-2xl hover:bg-blue-700 transition-colors text-base text-center block" data-id="element-1497">
                      Start your first sequence
                    </a>
                  </motion.div>}
              </div>
            </motion.div>}
        </AnimatePresence>
      </div>
    </section>;
}